<template>
  <div
    class="fixed top-0 inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4 transition-all duration-300"
    @click="$emit('close')"
  >
    <div
      class="bg-white rounded-2xl sm:rounded-3xl max-w-5xl w-full max-h-[95vh] sm:max-h-[92vh] overflow-hidden shadow-2xl transform transition-all duration-300 hover:shadow-3xl"
      @click.stop
    >
      <!-- Header - Responsive -->
      <div
        class="relative bg-gradient-to-r from-primary-300 via-primary-400 to-primary-500 px-4 sm:px-8 py-3"
      >
        <div class="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>

        <div class="relative flex items-center justify-between">
          <div class="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
            <div
              class="w-6 h-6 sm:w-8 sm:h-8 bg-white/20 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0"
            >
              <i class="fa-solid fa-history text-white text-sm sm:text-base"></i>
            </div>

            <div class="min-w-0 flex-1">
              <h2 class="text-lg sm:text-xl font-bold text-white truncate">
                Historique des tentatives
              </h2>
              <p class="text-blue-100 text-xs sm:text-sm mt-1 font-medium truncate">
                {{ exercice.title }}
              </p>
            </div>
          </div>

          <button
            @click="$emit('close')"
            class="w-8 h-8 sm:w-10 sm:h-10 cursor-pointer bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-200 rounded-lg sm:rounded-xl flex items-center justify-center group flex-shrink-0 ml-2"
          >
            <i
              class="fa-solid fa-times text-white group-hover:rotate-90 transition-transform duration-200 text-sm sm:text-base"
            ></i>
          </button>
        </div>
      </div>

      <div class="overflow-y-auto">
        <!-- Statistiques - Grid responsive -->
        <div class="px-4 sm:px-8 py-4 sm:py-6 bg-gradient-to-br from-gray-50 to-white">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
            <!-- Nombre de tentatives -->
            <div
              class="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
            >
              <div class="flex items-center justify-between">
                <div>
                  <div
                    class="text-2xl sm:text-3xl font-bold text-gray-700 group-hover:text-indigo-600 transition-colors"
                  >
                    {{ attempts.length }}
                  </div>
                  <div class="text-xs sm:text-sm text-gray-500 font-medium mt-1">
                    Tentative{{ attempts.length > 1 ? 's' : '' }}
                  </div>
                </div>
                <div
                  class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                >
                  <i class="fa-solid fa-list text-indigo-600 text-sm sm:text-lg"></i>
                </div>
              </div>
            </div>

            <!-- Meilleur score -->
            <div
              class="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
            >
              <div class="flex items-center justify-between">
                <div>
                  <div
                    class="text-2xl sm:text-3xl font-bold text-emerald-600 group-hover:text-emerald-700 transition-colors"
                  >
                    {{ bestScore?.toFixed(1) || '0' }}
                  </div>
                  <div class="text-xs sm:text-sm text-gray-500 font-medium mt-1">
                    Meilleur score
                  </div>
                </div>
                <div
                  class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-100 to-green-100 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                >
                  <i class="fa-solid fa-trophy text-emerald-600 text-sm sm:text-lg"></i>
                </div>
              </div>
            </div>

            <!-- Score moyen -->
            <div
              class="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group"
            >
              <div class="flex items-center justify-between">
                <div>
                  <div
                    class="text-2xl sm:text-3xl font-bold text-amber-600 group-hover:text-amber-700 transition-colors"
                  >
                    {{ averageScore?.toFixed(1) || '0' }}
                  </div>
                  <div class="text-xs sm:text-sm text-gray-500 font-medium mt-1">Score moyen</div>
                </div>
                <div
                  class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-100 to-yellow-100 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                >
                  <i class="fa-solid fa-chart-line text-amber-600 text-sm sm:text-lg"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-1 max-h-96 px-4 sm:px-8">
          <!-- État vide redesigné -->
          <div v-if="attempts.length === 0" class="text-center py-12 sm:py-16">
            <div
              class="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6"
            >
              <i class="fa-solid fa-inbox text-2xl sm:text-3xl text-gray-400"></i>
            </div>
            <h3 class="text-lg sm:text-xl font-bold text-gray-700 mb-2 sm:mb-3">
              Aucune tentative
            </h3>
            <p class="text-sm sm:text-base text-gray-500 max-w-sm mx-auto px-4">
              Vous n'avez pas encore essayé cet exercice. Commencez votre première tentative !
            </p>
          </div>

          <!-- Liste des tentatives - Layout mobile amélioré -->
          <div v-else class="space-y-3 sm:space-y-4 pb-4">
            <div
              v-for="(attempt, index) in attempts"
              :key="attempt.slug"
              class="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-indigo-200 group"
            >
              <!-- Layout mobile: Stack vertical -->
              <div
                class="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0"
              >
                <!-- Header de la tentative -->
                <div class="flex items-start space-x-3 sm:space-x-4 flex-1">
                  <!-- Badge numéroté -->
                  <div class="relative flex-shrink-0">
                    <div
                      class="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center text-white text-sm font-bold shadow-lg group-hover:scale-105 transition-transform duration-200"
                      :class="getAttemptStatusClass(attempt)"
                    >
                      {{ index + 1 }}
                    </div>
                    <!-- Indicateur de meilleur score -->
                    <div
                      v-if="attempt.score === bestScore"
                      class="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-yellow-400 rounded-full flex items-center justify-center"
                    >
                      <i class="fa-solid fa-crown text-yellow-800 text-xs"></i>
                    </div>
                  </div>

                  <!-- Info principales -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-start justify-between mb-2 sm:mb-3">
                      <div class="min-w-0 flex-1">
                        <h4
                          class="font-bold text-gray-900 group-hover:text-indigo-600 transition-colors text-sm sm:text-base"
                        >
                          Tentative #{{ index + 1 }}
                        </h4>
                        <p class="text-xs sm:text-sm text-gray-500 font-medium truncate">
                          {{ formatDate(attempt.created_at) }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Métriques - Layout mobile: 3 colonnes compactes -->
                <div
                  class="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-4 w-full sm:w-auto sm:min-w-0"
                >
                  <!-- Score -->
                  <div
                    class="flex flex-col sm:flex-row items-center sm:space-x-3 bg-gray-50 rounded-lg sm:rounded-xl p-2 sm:p-3 group-hover:bg-indigo-50 transition-colors"
                  >
                    <div
                      class="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0"
                    >
                      <i class="fa-solid fa-star text-white text-xs"></i>
                    </div>
                    <div class="text-center sm:text-left">
                      <div class="text-xs text-gray-500 font-medium">Score</div>
                      <div class="text-sm font-bold" :class="getScoreClass(attempt.score)">
                        {{ attempt.score !== null ? `${attempt.score.toFixed(1)}` : 'N/A' }}
                      </div>
                    </div>
                  </div>

                  <!-- Durée -->
                  <div
                    class="flex flex-col sm:flex-row items-center sm:space-x-3 bg-gray-50 rounded-lg sm:rounded-xl p-2 sm:p-3 group-hover:bg-indigo-50 transition-colors"
                  >
                    <div
                      class="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0"
                    >
                      <i class="fa-solid fa-clock text-white text-xs"></i>
                    </div>
                    <div class="text-center sm:text-left">
                      <div class="text-xs text-gray-500 font-medium">Durée</div>
                      <div class="text-sm font-bold text-gray-700">
                        {{ formatDuration(attempt) }}
                      </div>
                    </div>
                  </div>

                  <!-- Date relative -->
                  <div
                    class="flex flex-col sm:flex-row items-center sm:space-x-3 bg-gray-50 rounded-lg sm:rounded-xl p-2 sm:p-3 group-hover:bg-indigo-50 transition-colors"
                  >
                    <div
                      class="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-gray-400 to-gray-600 rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0"
                    >
                      <i class="fa-solid fa-calendar text-white text-xs"></i>
                    </div>
                    <div class="text-center sm:text-left">
                      <div class="text-xs text-gray-500 font-medium">Quand</div>
                      <div class="text-sm font-bold text-gray-700">
                        {{ formatRelativeTime(attempt.created_at) }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flex items-center justify-center w-full md:w-auto md:h-[60px]">
                  <!-- Bouton détails - Plus petit sur mobile -->
                  <button
                    class="flex justify-center items-center text-center w-full h-full cursor-pointer space-x-1 sm:space-x-2 bg-primary-500 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl text-xs sm:text-sm font-medium ml-2"
                    @click="$router.push('/exercice-results/' + attempt.slug)"
                  >
                    <i class="fa-solid fa-eye text-xs"></i>
                    <span class="">Détails</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer - Responsive -->
      <div
        class="px-4 sm:px-8 py-3 sm:py-2 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200"
      >
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0"
        >
          <div class="flex items-center justify-center sm:justify-start space-x-3">
            <div class="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></div>
            <span class="text-sm text-gray-600 font-medium">
              {{ attempts.length }} tentative{{ attempts.length > 1 ? 's' : '' }} au total
            </span>
          </div>

          <div class="flex items-center space-x-2 sm:space-x-4">
            <button
              @click="$emit('close')"
              class="flex-1 sm:flex-none px-4 sm:px-6 cursor-pointer py-2 sm:py-3 text-gray-600 bg-white border-2 border-gray-200 rounded-lg sm:rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 font-medium text-sm sm:text-base"
            >
              Fermer
            </button>
            <button
              @click="$emit('retry-from', null)"
              class="flex-1 sm:flex-none px-4 sm:px-6 cursor-pointer py-2 sm:py-3 bg-gradient-to-r from-primary-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-lg sm:rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 font-medium text-sm sm:text-base"
            >
              <i class="fa-solid fa-play text-sm"></i>
              <span>Nouvelle tentative</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, computed } from 'vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  exercice: {
    type: Object,
    required: true,
  },
  attempts: {
    type: Array,
    default: () => [],
  },
})

defineEmits(['close', 'view-result', 'retry-from'])

const { success } = useToast()

// Computed
const completedAttempts = computed(() => {
  return props.attempts.filter((attempt) => attempt.completed_at).length
})

const bestScore = computed(() => {
  const completed = props.attempts.filter(
    (attempt) => attempt.completed_at && attempt.score !== null,
  )
  if (completed.length === 0) return null
  return Math.max(...completed.map((attempt) => attempt.score))
})

const averageScore = computed(() => {
  const completed = props.attempts.filter(
    (attempt) => attempt.completed_at && attempt.score !== null,
  )
  if (completed.length === 0) return null
  const sum = completed.reduce((total, attempt) => total + attempt.score, 0)
  return sum / completed.length
})

// Methods
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatTime = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatRelativeTime = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

  if (diffInHours < 1) return "Il y a moins d'1h"
  if (diffInHours < 24) return `Il y a ${diffInHours}h`

  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}`

  const diffInWeeks = Math.floor(diffInDays / 7)
  return `Il y a ${diffInWeeks} semaine${diffInWeeks > 1 ? 's' : ''}`
}

const formatDuration = (attempt) => {
  if (!attempt.time_spent) return 'N/A'

  const minutes = Math.floor(attempt.time_spent / 60)
  const seconds = attempt.time_spent % 60

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`
  }
  return `${seconds}s`
}

const getAttemptStatusClass = (attempt) => {
  if (attempt.completed_at) {
    if (attempt.score >= 80) return 'bg-green-200'
    if (attempt.score >= 60) return 'bg-yellow-500'
    return 'bg-red-600'
  }
  return 'bg-gray-400'
}

const getStatusBadgeClass = (attempt) => {
  if (attempt.completed_at) {
    return 'bg-green-100 text-green-800'
  }
  return 'bg-gray-100 text-gray-800'
}

const getStatusText = (attempt) => {
  if (attempt.completed_at) return 'Terminée'
  return 'Non terminée'
}

const getScoreClass = (score) => {
  if (score === null) return 'text-gray-500'
  if (score >= 80) return 'text-green-600'
  if (score >= 60) return 'text-yellow-600'
  return 'text-red-600'
}

const getProgressBarClass = (score) => {
  if (score >= 80) return 'bg-green-500'
  if (score >= 60) return 'bg-yellow-500'
  return 'bg-red-500'
}

const shareAttempt = (attempt) => {
  const text = `J'ai obtenu ${attempt.score.toFixed(1)} à l'exercice "${props.exercice.title}"`

  if (navigator.share) {
    navigator.share({
      title: "Mon résultat d'exercice",
      text: text,
    })
  } else {
    navigator.clipboard.writeText(text)
    success('Résultat copié dans le presse-papiers')
  }
}
</script>

<style scoped>
/* Styles pour le scroll personnalisé */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
